<!-- src/views/CourseViewer.vue -->
<template>
  <section class="course-viewer section container" style="padding-top:140px">
    <div v-if="loading" class="glass" style="padding:20px">正在載入課程內容…</div>
    <div v-else-if="error" class="glass" style="padding:20px;color:#ffb3b3">無法載入課程：{{ error }}</div>

    <div v-else-if="course" class="cv-grid">
      <!-- ====== 左欄（桌面：固定；小視窗：抽屜） ====== -->
      <aside id="cv-sidebar" class="cv-sidebar glass" :class="{ 'is-drawer': true, 'open': drawerOpen }"
        aria-label="課程章節">
        <!-- 窄屏：內嵌右上角關閉鈕（無文字） -->
        <button v-if="drawerOpen" class="cv-close-inner" type="button" aria-label="關閉目錄"
          @click="drawerOpen = false">×</button>

        <h3 class="cv-title">📘 課程章節</h3>

        <div class="cv-sections">
          <section v-for="sec in groupedSections" :key="sec.key" class="cv-section">
            <button class="cv-sec-head" type="button" :aria-expanded="isOpen(sec.key)" @click="toggleSection(sec.key)">
              <span class="cv-sec-title">{{ sec.title || '未分類章節' }}</span>
              <span class="cv-sec-meta">
                {{ sec.lessons.length }} 節
                <svg class="cv-sec-caret" viewBox="0 0 24 24" :class="{ open: isOpen(sec.key) }">
                  <path d="M7 10l5 5 5-5z" />
                </svg>
              </span>
            </button>

            <ul v-show="isOpen(sec.key)" class="cv-list">
              <li v-for="item in sec.lessons" :key="item.lesson.id ?? item.globalIndex"
                :class="{ active: item.globalIndex === activeIndex }" @click="handlePlayFromDrawer(item.globalIndex)">
                <span class="cv-index">{{ item.globalIndex + 1 }}</span>
                <span class="cv-lesson">{{ item.lesson.title }}</span>
              </li>
            </ul>
          </section>
        </div>
      </aside>

      <!-- 小視窗遮罩（點擊關閉） -->
      <div class="cv-dim" v-show="drawerOpen" @click="drawerOpen = false" aria-hidden="true"></div>

      <!-- ====== 右欄 ====== -->
      <main class="cv-main glass">
        <!-- 小視窗：目錄切換按鈕（靠左） -->
        <div class="cv-topbar">
          <button class="btn btn--light cv-toggle" type="button" @click="drawerOpen = true" aria-controls="cv-sidebar"
            :aria-expanded="drawerOpen">
            ☰ 目錄
          </button>
        </div>

        <h1 class="h1" style="margin:8px 0 12px">{{ course.title }}</h1>
        <p class="lead" style="opacity:.85;margin:0 0 18px">
          {{ currentLessonTitle || '課程簡介' }}
        </p>
        
        <!-- 影片區 -->
        <div class="cv-video" :class="{ locked: isLocked }">
          <template v-if="isLocked">
            <div class="cv-locked">
              <div class="cv-locked__panel">
                <svg viewBox="0 0 24 24" class="lock-ic">
                  <path
                    d="M12 2a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1V7a5 5 0 0 0-5-5zm-3 8V7a3 3 0 0 1 6 0v3H9z" />
                </svg>
                <h4 class="locked-title">此影片為付費內容</h4>
                <p class="locked-desc">購買課程後即可解鎖全部章節與附件</p>
                <router-link :to="purchaseHref" class="btn btn--primary locked-btn">
                  購買課程
                </router-link>
              </div>
            </div>
          </template>

          <div v-if="!isLocked && embedSrc" class="cv-spinner" v-show="videoLoading">
            <div class="spinner"></div>
          </div>

          <iframe v-if="!isLocked && embedSrc" :key="activeIndex" :src="embedSrc" title="課程影片" frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            loading="lazy" allowfullscreen @load="onIframeLoad" />
          <div v-else-if="!isLocked" class="glass cv-empty">此課程尚未設定影片連結</div>
        </div>

        <p style="color: #ffb3b3;">YouTube資源， 部分地區無法觀看，需要解決網路問題</p>
        <!-- 課件下載 -->
        <!-- 課件下載：大卡片樣式 -->
        <section v-if="attachments && attachments.length" class="cv-files">
          <div class="cv-files__head">
            <div class="cv-files__titles">
              <h3 class="cv-files__title">
                📦 課程附件下載
              </h3>
              <p class="cv-files__subtitle">
                本節課共有 <strong>{{ attachments.length }}</strong> 份課件，點擊即可在新視窗開啟或下載。
              </p>
            </div>

            <!-- 右側小提示 -->
            <div class="cv-files__meta">
              <span class="cv-files__badge">本節課專屬</span>
            </div>
          </div>

          <div class="cv-files__grid">
            <a v-for="(f, i) in attachments" :key="i" :href="f.url" target="_blank" rel="noopener noreferrer"
              class="cv-file-card">
              <!-- 左：圓形圖標 -->
              <div class="cv-file-card__icon-wrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"
                  stroke-linejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M12 3v6m0 3v9M12 9h2m-2-3h2m-2-3h2" />
                </svg>




                <span class="cv-file-card__index">#{{ i + 1 }}</span>
              </div>

              <!-- 中：檔名 + URL 簡略 -->
              <div class="cv-file-card__body">
                <div class="cv-file-card__name" :title="f.name || f.url">
                  {{ f.name || f.url }}
                </div>
                <div class="cv-file-card__url" :title="f.url">
                  {{ f.url }}
                </div>
              </div>

              <!-- 右：下載圖標按鈕感 -->
              <div class="cv-file-card__action">
                <svg class="cv-file-card__download" viewBox="0 0 24 24">
                  <path d="M12 4v12m0 0l-4-4m4 4l4-4M5 20h14" />
                </svg>
                <span class="cv-file-card__action-text">下載</span>
              </div>
            </a>
          </div>
        </section>
      </main>
    </div>

    <div v-else class="glass" style="padding:20px">找不到此課程。</div>
  </section>
</template>

<script setup>
import { computed, ref, onMounted, watch, reactive, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { get } from '@/utils/http'

const route = useRoute()
const slugRef = ref(String(route.params.id ?? route.params.slug ?? ''))
const course = ref(null)
const activeIndex = ref(0)
const loading = ref(true)
const error = ref('')

// 抽屜狀態（小視窗）
const drawerOpen = ref(false)

// 影片載入動畫
const videoLoading = ref(true)
function onIframeLoad() {
  videoLoading.value = false
}

async function loadCourse() {
  loading.value = true
  try {
    const data = await get(`/api/courses/${encodeURIComponent(slugRef.value)}/viewer`)
    course.value = data?.course ?? null
    videoLoading.value = true
  } catch (e) {
    error.value = e?.message || 'UNKNOWN_ERROR'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadCourse()
  window.addEventListener('keydown', onKeydown)
  window.addEventListener('resize', onResize, { passive: true })
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
  window.removeEventListener('resize', onResize)
  document.body.classList.remove('no-scroll', 'cv-drawer-open')
})

function onKeydown(e) {
  if (e.key === 'Escape' && drawerOpen.value) drawerOpen.value = false
}
function onResize() {
  if (window.innerWidth > 1024 && drawerOpen.value) {
    drawerOpen.value = false
    document.body.classList.remove('no-scroll', 'cv-drawer-open')
  }
}

// 抽屜開關 → 鎖背景滾動
watch(drawerOpen, (v) => {
  document.body.classList.toggle('no-scroll', !!v)
  document.body.classList.toggle('cv-drawer-open', !!v)
})

watch(() => route.params.id, v => { slugRef.value = String(v ?? ''); loadCourse() })
watch(() => route.params.slug, v => { slugRef.value = String(v ?? ''); loadCourse() })

// ===== 章節分組 =====
const groupedSections = computed(() => {
  const c = course.value
  if (!c?.lessons?.length) return []
  const buckets = new Map(), order = []
  c.lessons.forEach((lesson, idx) => {
    const key = lesson.sectionId ? `s-${lesson.sectionId}` : 'uncat'
    if (!buckets.has(key)) {
      buckets.set(key, { key, title: lesson.sectionTitle || '', lessons: [] })
      order.push(key)
    }
    buckets.get(key).lessons.push({ lesson, globalIndex: idx })
  })
  return order.map(k => buckets.get(k))
})

// 預設展開所有章節
const openState = reactive(new Set())
watch(groupedSections, (secs) => {
  openState.clear()
  for (const s of secs) openState.add(s.key)
}, { immediate: true })
function toggleSection(k) { openState.has(k) ? openState.delete(k) : openState.add(k) }
function isOpen(k) { return openState.has(k) }

// ===== 影片 / 權限 =====
function toEmbed(u) {
  if (!u) return ''
  if (u === 'LOCKED') return 'LOCKED'
  try {
    const url = new URL(u)
    if (url.hostname.includes('youtu')) {
      const vid = url.pathname.includes('/embed/')
        ? url.pathname.split('/embed/')[1]
        : url.searchParams.get('v') || url.pathname.slice(1)
      if (!vid) return ''
      return `https://www.youtube.com/embed/${vid}?rel=0&modestbranding=1&playsinline=1`
    }
  } catch (err) {
    return u
  }
  return u
}

const currentVideo = computed(() => {
  const c = course.value
  if (!c) return ''
  return c.lessons?.[activeIndex.value]?.videoUrl || c.videoUrl || ''
})

const embedSrc = computed(() => toEmbed(currentVideo.value))
watch(embedSrc, () => { videoLoading.value = true })

const currentLessonTitle = computed(
  () => course.value?.lessons?.[activeIndex.value]?.title || ''
)

// ✅ 是否上鎖（沒買課程時，後端會把 videoUrl 設為 "LOCKED"）
const isLocked = computed(() => currentVideo.value === 'LOCKED' || embedSrc.value === 'LOCKED')

// 購買連結
const purchaseHref = computed(
  () => `/purchase?course=${encodeURIComponent(String(course.value?.id ?? slugRef.value))}`
)

// ✅ 使用「當前小節」的 attachments（來自 lessons.attachments_json）
const attachments = computed(() => {
  const c = course.value
  if (!c?.lessons?.length) return []
  const l = c.lessons[activeIndex.value]
  return l?.attachments || []
})

// 點選清單播放（小視窗同時關閉抽屜）
function play(i) {
  activeIndex.value = i
}
function handlePlayFromDrawer(i) {
  play(i)
  drawerOpen.value = false
}


</script>


<style scoped>
:global(:root) {
  --cv-nav-h: 64px;
}

/* 可保留，抽屛已不依賴它 */

/* 抽屜開啟時，壓低導覽欄與頁腳的疊層 + 關閉點擊 */
:global(body.cv-drawer-open .navbar),
:global(body.cv-drawer-open .site-footer),
:global(body.cv-drawer-open footer) {
  z-index: 0 !important;
  pointer-events: none !important;
}

/* ===== 版型 ===== */
.cv-grid {
  display: grid;
  gap: 24px;
  grid-template-columns: 280px 1fr;
  align-items: start
}

/* ===== 左側欄（桌面） ===== */
.cv-sidebar {
  padding: 20px 16px;
  border-radius: 16px;
  position: sticky;
  top: var(--cv-nav-h, 64px);
  /* CHANGED: 緊貼導覽欄 */
  max-height: calc(100vh - var(--cv-nav-h, 64px) - 32px);
  /* CHANGED: 視窗高 - 導覽欄 - 些許底部緩衝 */
  overflow-y: auto;
}

/* ADDED: 寬屏也使用與窄屏一致的滾動條樣式 */
.cv-sidebar::-webkit-scrollbar {
  width: 12px
}

.cv-sidebar::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, .06);
  border-radius: 12px
}

.cv-sidebar::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, rgba(255, 255, 255, .45), rgba(255, 255, 255, .25));
  border-radius: 12px;
  border: 2px solid transparent;
  background-clip: padding-box;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, .12);
}

.cv-sidebar::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, rgba(255, 255, 255, .6), rgba(255, 255, 255, .35));
}

.cv-sidebar {
  scrollbar-color: rgba(255, 255, 255, .35) rgba(255, 255, 255, .06);
  scrollbar-width: thin;
}

.cv-title {
  margin: 0 0 14px;
  font-size: 16px;
  font-weight: 800;
  color: #fff
}

/* ===== 章節折疊 ===== */
.cv-sections {
  display: grid;
  gap: 12px
}

.cv-section {
  border: 1px solid rgba(255, 255, 255, .12);
  border-radius: 14px;
  background: rgba(255, 255, 255, .06);
  overflow: hidden
}

.cv-sec-head {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 10px 14px;
  padding: 14px 16px;
  background: rgba(255, 255, 255, .1);
  border: 0;
  color: #fff;
  cursor: pointer;
  text-align: left;
  transition: .2s
}

.cv-sec-head:hover {
  background: rgba(255, 255, 255, .15)
}

.cv-sec-title {
  font-weight: 800;
  font-size: 15px;
  line-height: 1.35
}

.cv-sec-meta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 600;
  opacity: .9
}

.cv-sec-caret {
  width: 16px;
  height: 16px;
  fill: currentColor;
  transition: transform .2s
}

.cv-sec-caret.open {
  transform: rotate(180deg)
}

.cv-list {
  list-style: none;
  padding: 8px;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px
}

.cv-list li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  color: rgba(255, 255, 255, .9);
  cursor: pointer;
  transition: .2s
}

.cv-list li:hover {
  background: rgba(255, 255, 255, .12)
}

.cv-list li.active {
  background: rgba(255, 255, 255, .25);
  font-weight: 700;
  color: #fff
}

.cv-index {
  font-size: 12px;
  opacity: .8;
  width: 20px;
  text-align: center
}

.cv-lesson {
  flex: 1;
  font-size: 14px;
  text-align: left
}

/* ===== 右欄 ===== */
.cv-main {
  border-radius: 16px;
  padding: 20px;
  min-height: 480px
}

.cv-topbar {
  display: none;
  margin-bottom: 10px;
}

.cv-toggle {
  padding: 8px 12px;
  border-radius: 10px;
  font-weight: 700
}

/* ===== 影片區 ===== */
/* CHANGED: 去除玻璃/邊框/重陰影；保持圓角、比例 */
.cv-video {
  position: relative;
  aspect-ratio: 16/9;
  background: transparent;
  /* CHANGED */
  border-radius: 12px;
  overflow: hidden;
  box-shadow: none;
  /* CHANGED */
  margin-bottom: 16px;
  border: none;
  /* CHANGED（若有外部樣式） */
}

.cv-video>iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0
}

/* 影片載入中小動畫 */
.cv-spinner {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  pointer-events: none
}

.spinner {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 4px solid rgba(255, 255, 255, .35);
  border-top-color: #fff;
  animation: sp 0.9s linear infinite;
}

@keyframes sp {
  to {
    transform: rotate(360deg)
  }
}

/* 鎖定狀態 */
.cv-locked {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(80, 80, 80, .55);
  backdrop-filter: blur(10px) saturate(1.2);
  -webkit-backdrop-filter: blur(10px) saturate(1.2);
}

.cv-locked__panel {
  text-align: center;
  color: #fff;
  padding: 24px 26px;
  border-radius: 16px;
  background: rgba(255, 255, 255, .15);
  border: 1px solid rgba(255, 255, 255, .2);
  box-shadow: 0 10px 30px rgba(0, 0, 0, .35)
}

.lock-ic {
  width: 40px;
  height: 40px;
  fill: #fff;
  opacity: .95;
  margin-bottom: 10px
}

.locked-title {
  margin: 4px 0 6px;
  font-size: 18px;
  font-weight: 800
}

.locked-desc {
  margin: 0 0 14px;
  opacity: .9
}

.locked-btn {
  padding: 10px 18px;
  border-radius: 10px;
  font-weight: 700
}

/* 停止背景滾動（抽屜開啟時會加到 <body>） */
:global(body.no-scroll) {
  overflow: hidden;
  height: 100vh;
}

/* ===== 小視窗（抽屜）— 覆蓋導航欄與頁腳 ===== */
@media (max-width: 1024px) {
  .cv-grid {
    grid-template-columns: 1fr;
  }

  .cv-topbar {
    display: flex;
    justify-content: flex-start
  }

  /* 抽屜：覆蓋整個視窗（含導航欄與頁腳） */
  .cv-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    max-height: none;
    width: 86vw;
    max-width: 360px;
    margin: 0;
    border-radius: 0 16px 16px 0;
    transform: translateX(-100%);
    transition: transform .25s ease;
    z-index: 10010;
    /* 高於導覽與頁腳 */
    overflow-y: auto;
    /* 只滾側欄 */
    -webkit-overflow-scrolling: touch;
    scrollbar-gutter: stable both-edges;
    padding-top: 12px;
    /* 騰出一點空間給關閉鈕 */
  }

  .cv-sidebar.open {
    transform: translateX(0);
  }

  /* 內嵌右上角關閉按鈕（無文字） */
  .cv-close-inner {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 38px;
    height: 38px;
    display: grid;
    place-items: center;
    border: 1px solid rgba(255, 255, 255, .35);
    border-radius: 50%;
    background: rgba(40, 40, 40, .45);
    backdrop-filter: blur(6px) saturate(1.2);
    -webkit-backdrop-filter: blur(6px) saturate(1.2);
    color: #fff;
    font-size: 18px;
    font-weight: 700;
    cursor: pointer;
    z-index: 10020;
    transition: background .2s, transform .2s, opacity .2s;
  }

  .cv-close-inner:hover {
    background: rgba(255, 255, 255, .25);
    transform: scale(1.05);
  }

  /* 全螢幕遮罩（覆蓋導航欄與頁腳；點擊關閉） */
  .cv-dim {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, .45);
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
    z-index: 10000;
    /* 比側欄低、但高於背景 */
  }

  /* 窄屏美化側欄滾動條（保留） */
  .cv-sidebar::-webkit-scrollbar {
    width: 12px
  }

  .cv-sidebar::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, .06);
    border-radius: 12px
  }

  .cv-sidebar::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, rgba(255, 255, 255, .45), rgba(255, 255, 255, .25));
    border-radius: 12px;
    border: 2px solid transparent;
    background-clip: padding-box;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, .12);
  }

  .cv-sidebar::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(180deg, rgba(255, 255, 255, .6), rgba(255, 255, 255, .35));
  }

  .cv-sidebar {
    scrollbar-color: rgba(255, 255, 255, .35) rgba(255, 255, 255, .06);
    scrollbar-width: thin;
  }

  .cv-close-inner {
    display: grid !important;
  }

  /* 防被外部樣式覆蓋 */
}

/* ===== 課件下載區：大卡片樣式 ===== */
.cv-files {
  margin-top: 24px;
  padding: 18px 20px 20px;
  border-radius: 16px;
  background: radial-gradient(circle at top left,
      rgba(255, 255, 255, .18),
      rgba(0, 0, 0, .6));
  border: 1px solid rgba(255, 255, 255, .16);
  box-shadow: 0 16px 40px rgba(0, 0, 0, .45);
}

.cv-files__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.cv-files__titles {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cv-files__title {
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  color: #fff;
  letter-spacing: .03em;
}

.cv-files__subtitle {
  margin: 0;
  font-size: 13px;
  opacity: .9;
  color: rgba(255, 255, 255, .9);
}

.cv-files__subtitle strong {
  font-weight: 800;
  color: #fff;
}

/* 右上角小徽章 */
.cv-files__meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cv-files__badge {
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, .4);
  background: linear-gradient(135deg, rgba(255, 255, 255, .22), rgba(255, 255, 255, .06));
  color: #fff;
  text-transform: uppercase;
  letter-spacing: .04em;
}

/* 卡片網格 */
.cv-files__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px 14px;
  margin-top: 6px;
}

/* 單張卡片 */
.cv-file-card {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 10px 12px;
  padding: 10px 12px;
  border-radius: 14px;
  text-decoration: none;
  color: #fff;
  background: rgba(0, 0, 0, .45);
  border: 1px solid rgba(255, 255, 255, .16);
  backdrop-filter: blur(10px) saturate(1.2);
  -webkit-backdrop-filter: blur(10px) saturate(1.2);
  transition:
    transform .18s ease,
    box-shadow .18s ease,
    background .18s ease,
    border-color .18s ease;
}

.cv-file-card:hover {
  transform: translateY(-2px);
  background: rgba(0, 0, 0, .6);
  border-color: rgba(255, 255, 255, .6);
  box-shadow: 0 14px 32px rgba(0, 0, 0, .6);
}

/* 左側圖標圓圈 */
.cv-file-card__icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  position: relative;
  background: radial-gradient(circle at 20% 0, rgba(255, 255, 255, .8), rgba(255, 255, 255, .1));
  box-shadow: 0 0 0 1px rgba(255, 255, 255, .25);
}

.cv-file-card__icon {
  width: 22px;
  height: 22px;
  fill: #111;
}

.cv-file-card__index {
  position: absolute;
  bottom: -4px;
  right: -4px;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 999px;
  background: rgba(0, 0, 0, .9);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, .6);
}

/* 中間文字區域 */
.cv-file-card__body {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cv-file-card__name {
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.cv-file-card__url {
  font-size: 11px;
  opacity: .9;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  color: rgba(255, 255, 255, .75);
}

/* 右側動作區 */
.cv-file-card__action {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  font-size: 11px;
}

.cv-file-card__download {
  width: 20px;
  height: 20px;
  stroke: none;
  fill: #fff;
}

.cv-file-card__action-text {
  font-size: 11px;
  letter-spacing: .04em;
  text-transform: uppercase;
  opacity: .9;
}

/* 窄屏時整體留一點邊距 */
@media (max-width: 640px) {
  .cv-files {
    margin-left: -4px;
    margin-right: -4px;
    border-radius: 14px;
  }
}

.cv-file-card[data-type="pdf"] .cv-file-card__icon-wrap {
  background: rgba(255, 83, 83, .2);
  border-color: rgba(255, 83, 83, .4);
}

.cv-file-card[data-type="zip"] .cv-file-card__icon-wrap {
  background: rgba(255, 199, 67, .25);
  border-color: rgba(255, 199, 67, .5);
}

.cv-file-card[data-type="ppt"] .cv-file-card__icon-wrap {
  background: rgba(255, 145, 51, .25);
  border-color: rgba(255, 145, 51, .5);
}

.cv-file-card[data-type="doc"] .cv-file-card__icon-wrap {
  background: rgba(47, 154, 255, .25);
  border-color: rgba(47, 154, 255, .5);
}

.cv-file-card__icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, .28);
  background: rgba(255, 255, 255, .03);
}

.cv-file-card__icon {
  width: 22px;
  height: 22px;
  color: #fff;
}
</style>
